/**
 * Curated South African address data for intelligent auto-completion
 * This data provides a foundation for address validation and suggestions
 */

export interface SACity {
  name: string;
  suburbs: string[];
  postalCodes: string[];
}

export interface SAProvince {
  name: string;
  code: string;
  cities: Record<string, SACity>;
}

export const southAfricanAddresses: Record<string, SAProvince> = {
  "Western Cape": {
    name: "Western Cape",
    code: "WC",
    cities: {
      "Cape Town": {
        name: "Cape Town",
        suburbs: [
          "City Bowl", "Sea Point", "Green Point", "Camps Bay", "Clifton", "Bantry Bay",
          "Tamboerskloof", "Gardens", "Oranjezicht", "Higgovale", "Bo-Kaap", "Woodstock",
          "Observatory", "Salt River", "Mowbray", "Rosebank", "Rondebosch", "Newlands",
          "Claremont", "Wynberg", "Plumstead", "Diep River", "Steenberg", "Muizenberg",
          "Fish Hoek", "Kalk Bay", "St James", "Simon's Town", "Hout Bay", "Llandudno",
          "Noordhoek", "Kommetjie", "Scarborough", "Bellville", "Parow", "Goodwood",
          "Elsies River", "Brackenfell", "Kraaifontein", "Kuils River", "Blue Downs",
          "Mitchells Plain", "Khayelitsha", "Gugulethu", "Nyanga", "Philippi", "Grassy Park",
          "Lotus River", "Ottery", "Bergvliet", "Tokai", "Constantia", "Bishopscourt"
        ],
        postalCodes: ["8000", "8001", "8002", "8005", "8010", "8012", "8018", "8020"]
      },
      "Stellenbosch": {
        name: "Stellenbosch",
        suburbs: ["Central", "Die Boord", "Cloetesville", "Kayamandi", "Idas Valley", "Welgevonden"],
        postalCodes: ["7600", "7601", "7602"]
      },
      "Paarl": {
        name: "Paarl",
        suburbs: ["Central", "Dal Josafat", "Klein Drakenstein", "Wellington", "Mbekweni"],
        postalCodes: ["7620", "7622", "7624"]
      },
      "George": {
        name: "George",
        suburbs: ["Central", "Heather Park", "Blanco", "Thembalethu", "Pacaltsdorp"],
        postalCodes: ["6529", "6530", "6531"]
      }
    }
  },
  "Gauteng": {
    name: "Gauteng",
    code: "GP",
    cities: {
      "Johannesburg": {
        name: "Johannesburg",
        suburbs: [
          "City Centre", "Braamfontein", "Hillbrow", "Berea", "Yeoville", "Observatory",
          "Doornfontein", "Jeppestown", "New Doornfontein", "Fordsburg", "Mayfair",
          "Pageview", "Vrededorp", "Newtown", "Marshalltown", "Ferreirasdorp",
          "Sandton", "Morningside", "Rivonia", "Bryanston", "Rosebank", "Hyde Park",
          "Melrose", "Illovo", "Sandhurst", "Atholl", "Inanda", "Bramley", "Dunkeld",
          "Craighall", "Parktown", "Westcliff", "Parkview", "Greenside", "Emmarentia",
          "Linden", "Auckland Park", "Melville", "Richmond", "Sophiatown", "Triomf",
          "Northcliff", "Fairland", "Crosby", "Blackheath", "Little Falls", "Randburg",
          "Blairgowrie", "Bordeaux", "Northgate", "Ferndale", "Randpark Ridge",
          "Roodepoort", "Honeydew", "Weltevreden Park", "Ruimsig", "Constantia Kloof"
        ],
        postalCodes: ["2000", "2001", "2017", "2092", "2094", "2196", "2146", "2191"]
      },
      "Pretoria": {
        name: "Pretoria",
        suburbs: [
          "City Centre", "Arcadia", "Hatfield", "Brooklyn", "Menlo Park", "Lynnwood",
          "Garsfontein", "Waverley", "Waterkloof", "Groenkloof", "Lukasrand", "Erasmuskloof",
          "Eldoraigne", "Centurion", "Irene", "Lyttelton", "Wierdapark", "Clubview",
          "Doornpoort", "Annlin", "Wonderboom", "Ninapark", "Karenpark", "Tileba",
          "Sunnyside", "Salvokop", "Mamelodi", "Atteridgeville", "Laudium", "Eersterust"
        ],
        postalCodes: ["0001", "0002", "0007", "0028", "0081", "0083", "0157", "0181"]
      }
    }
  },
  "KwaZulu-Natal": {
    name: "KwaZulu-Natal",
    code: "KZN",
    cities: {
      "Durban": {
        name: "Durban",
        suburbs: [
          "City Centre", "Point", "Beachfront", "Morningside", "Berea", "Musgrave",
          "Glenwood", "Umbilo", "Greyville", "Stamfordhill", "Windermere", "Kloof",
          "Hillcrest", "Westville", "Pinetown", "Queensburgh", "Chatsworth", "Phoenix",
          "Verulam", "Inanda", "KwaMashu", "Ntuzuma", "Umlazi", "Isipingo", "Amanzimtoti",
          "Kingsburgh", "Warner Beach", "Scottburgh", "Ballito", "Umhlanga", "La Lucia"
        ],
        postalCodes: ["4000", "4001", "4013", "4051", "4091", "4319", "4320"]
      },
      "Pietermaritzburg": {
        name: "Pietermaritzburg",
        suburbs: ["Central", "Scottsville", "Wembley", "Hayfields", "Northdale", "Edendale"],
        postalCodes: ["3200", "3201", "3202"]
      }
    }
  },
  "Eastern Cape": {
    name: "Eastern Cape", 
    code: "EC",
    cities: {
      "Port Elizabeth": {
        name: "Port Elizabeth",
        suburbs: ["Central", "Summerstrand", "Humewood", "Walmer", "Greenacres", "Newton Park"],
        postalCodes: ["6000", "6001", "6019"]
      },
      "East London": {
        name: "East London",
        suburbs: ["Central", "Beacon Bay", "Vincent", "Southernwood", "Cambridge", "Nahoon"],
        postalCodes: ["5200", "5201", "5247"]
      }
    }
  },
  "Free State": {
    name: "Free State",
    code: "FS", 
    cities: {
      "Bloemfontein": {
        name: "Bloemfontein",
        suburbs: ["Central", "Waverley", "Willows", "Universitas", "Bainsvlei", "Mangaung"],
        postalCodes: ["9300", "9301", "9307"]
      }
    }
  },
  "Mpumalanga": {
    name: "Mpumalanga",
    code: "MP",
    cities: {
      "Nelspruit": {
        name: "Nelspruit", 
        suburbs: ["Central", "West Acres", "Riverside", "Steiltes", "KaNyamazane"],
        postalCodes: ["1200", "1201", "1204"]
      }
    }
  },
  "Limpopo": {
    name: "Limpopo",
    code: "LP",
    cities: {
      "Polokwane": {
        name: "Polokwane",
        suburbs: ["Central", "Bendor", "Flora Park", "Welgelegen", "Seshego"],
        postalCodes: ["0700", "0699", "0742"]
      }
    }
  },
  "North West": {
    name: "North West",
    code: "NW", 
    cities: {
      "Rustenburg": {
        name: "Rustenburg",
        suburbs: ["Central", "Cashan", "Boitekong", "Tlhabane", "Phokeng"],
        postalCodes: ["0300", "0299", "0308"]
      }
    }
  },
  "Northern Cape": {
    name: "Northern Cape",
    code: "NC",
    cities: {
      "Kimberley": {
        name: "Kimberley", 
        suburbs: ["Central", "Beaconsfield", "Hadison Park", "Galeshewe", "Royldene"],
        postalCodes: ["8300", "8301", "8345"]
      }
    }
  }
};

/**
 * Postal code patterns for South African provinces
 */
export const saPostalCodePatterns = {
  "Western Cape": /^[78]\d{3}$/,
  "Gauteng": /^[012]\d{3}$/,
  "KwaZulu-Natal": /^[34]\d{3}$/,
  "Eastern Cape": /^[56]\d{3}$/,
  "Free State": /^9[0-4]\d{2}$/,
  "Mpumalanga": /^1[2-3]\d{2}$/,
  "Limpopo": /^[07]\d{3}$/,
  "North West": /^[02-3]\d{3}$/,
  "Northern Cape": /^8[3-4]\d{2}$/,
};